<template>
    <div class="container">
        <!-- Heading Section -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h1 class="text-8 text-black mb-4">
                        Maximize Your Savings Potential with Our Savings Growth Calculator
                    </h1>
                    <p class="text-3">
                        Planning ahead is a cornerstone of financial success, and our Savings Growth Calculator makes it easier than ever. Whether you’re saving for a home, education, retirement, or an emergency fund, this tool helps you project how your savings can grow over time, empowering you to make informed decisions and achieve your goals faster.
                    </p>
                </div>
            </div>
        </div>

        <!-- Why Use a Savings Growth Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h2 class="text-7 text-black mb-4">
                        Why Use a Savings Growth Calculator?
                    </h2>
                    <p class="text-3">
                        Savings are the foundation of financial security. Understanding how your money can grow with interest, contributions, and time is essential for effective financial planning. Here’s how our Savings Growth Calculator helps:
                    </p>
                    <ul class="text-3">
                        <li><strong>Accurate Projections:</strong> Calculate the future value of your savings with precision.</li>
                        <li><strong>Customizable Inputs:</strong> Adjust interest rates, contributions, and timeframes to see different outcomes.</li>
                        <li><strong>Time-Saving Tool:</strong> Get instant results to plan your financial future with confidence.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- How the Savings Growth Calculator Works -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h2 class="text-7 text-black mb-4">
                        How the Savings Growth Calculator Works
                    </h2>
                    <p class="text-3">
                        Using the Savings Growth Calculator is simple:
                    </p>
                    <ol class="text-3">
                        <li><strong>Enter Your Starting Amount:</strong> Input the initial amount you’ve saved or plan to save.</li>
                        <li><strong>Set the Interest Rate:</strong> Enter the annual interest rate you expect to earn.</li>
                        <li><strong>Add Regular Contributions:</strong> Specify how much you plan to contribute monthly, quarterly, or annually.</li>
                        <li><strong>Choose Your Timeframe:</strong> Select the duration for which you’ll be saving.</li>
                        <li><strong>Calculate:</strong> Instantly view the projected growth of your savings.</li>
                    </ol>
                </div>
            </div>
        </div>

        <!-- Key Features of the Savings Growth Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Key Features of the Savings Growth Calculator
                    </h3>
                    <ul class="text-3">
                        <li><strong>Future Value Calculation:</strong> See the total value of your savings after accounting for interest and contributions.</li>
                        <li><strong>Compound Interest Insights:</strong> Understand how compounding accelerates your savings growth.</li>
                        <li><strong>Flexible Scenarios:</strong> Adjust variables to explore different savings strategies.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Who Can Benefit from a Savings Growth Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Who Can Benefit from a Savings Growth Calculator?
                    </h3>
                    <ul class="text-3">
                        <li><strong>First-Time Savers:</strong> Start small and see how even modest contributions grow over time.</li>
                        <li><strong>Parents:</strong> Plan for your child’s education or future expenses.</li>
                        <li><strong>Professionals:</strong> Save for retirement, business investments, or personal goals.</li>
                        <li><strong>Entrepreneurs:</strong> Build a safety net for your business with strategic savings.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Benefits of Using the Savings Growth Calculator -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        The Benefits of Using Our Savings Growth Calculator
                    </h3>
                    <ul class="text-3">
                        <li><strong>Clarity:</strong> Visualize how small changes in contributions or interest rates impact your savings.</li>
                        <li><strong>Motivation:</strong> See the long-term rewards of consistent saving.</li>
                        <li><strong>Planning:</strong> Set realistic goals and track your progress over time.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- The Power of Compound Interest -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        The Power of Compound Interest
                    </h3>
                    <p class="text-3">
                        One of the most powerful tools for growing your savings is compound interest. Unlike simple interest, which is calculated only on the principal amount, compound interest grows on both the principal and the accumulated interest. Here’s how it works:
                    </p>
                    <ul class="text-3">
                        <li><strong>Year 1:</strong> You earn interest on your initial savings.</li>
                        <li><strong>Year 2:</strong> You earn interest on your initial savings and the interest earned in Year 1.</li>
                        <li><strong>Year 3 and Beyond:</strong> The cycle continues, creating exponential growth.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Important Areas That Need Savings -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Important Areas That Need Savings
                    </h3>
                    <ul class="text-3">
                        <li><strong>Building an Emergency Fund:</strong> See how quickly you can save for unexpected expenses by setting aside a portion of your income each month. Emergency funds turn an emergency into an inconvenience.</li>
                        <li><strong>Saving for Education:</strong> Project how much you’ll need to save for school fees or college tuition.</li>
                        <li><strong>Retirement Planning:</strong> Estimate the value of your retirement savings based on your contributions and expected returns.</li>
                        <li><strong>Buying a Home:</strong> Calculate how long it will take to save for a down payment.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Tips for Maximizing Your Savings Growth -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Tips for Maximizing Your Savings Growth
                    </h3>
                    <ul class="text-3">
                        <li><strong>Start Early:</strong> The earlier you start saving, the more time your money has to grow.</li>
                        <li><strong>Be Consistent:</strong> Regular contributions, even small ones, add up over time.</li>
                        <li><strong>Take Advantage of High-Interest Accounts:</strong> Look for accounts that offer competitive interest rates.</li>
                        <li><strong>Review Your Goals:</strong> Periodically reassess your savings plan to ensure it aligns with your financial goals.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- How Our Calculator Supports Your Financial Goals -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        How Our Calculator Supports Your Financial Goals
                    </h3>
                    <p class="text-3">
                        Saving is about more than just setting money aside; it’s about creating a strategy to achieve your dreams. Our Savings Growth Calculator empowers you to:
                    </p>
                    <ul class="text-3">
                        <li><strong>Visualize Progress:</strong> See how your savings grow year by year.</li>
                        <li><strong>Stay On Track:</strong> Use the calculator to monitor your progress and adjust as needed.</li>
                        <li><strong>Achieve Your Goals:</strong> Whether it’s a dream vacation, a new car, or financial independence, our calculator helps you get there.</li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Savings Growth Calculator: Your Partner in Financial Success -->
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Savings Growth Calculator: Your Partner in Financial Success
                    </h3>
                    <p class="text-3">
                        At Magetsi, we’re committed to empowering individuals and families to take control of their financial futures. Our Savings Growth Calculator is just one of the many tools we offer to simplify financial planning and make your goals achievable.
                    </p>
                    <button @click="scrollToTop()" class="btn btn-primary">
                        Start Growing Your Savings Today
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    methods: {
        scrollToTop() {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }
}
</script>

<style scoped>
/* Add custom styles here */
.container {
    padding: 20px;
}

h1, h2, h3 {
    color: #333;
    font-size: 1.8rem;
}

.text-3 {
    font-size: 1.2rem;
}

.text-6 {
    font-size: 1.5rem;
}

.text-7 {
    font-size: 1.7rem;
}

.text-8 {
    font-size: 2rem;
}

button {
    margin-top: 20px;
}
</style>
